// FILMUL împachetării, PESTE planșa de parcaj — un cadru pe pas: rețeaua de
// alei după pasul ales, cu aleile NOI îngroșate, cele scoase punctate roșu, și
// locurile pe care le purta rețeaua atunci. E singurul mod de a vedea CE a
// făcut un pas: tabelul de pași spune doar câte locuri au rămas după el.
//
// În metri, în cadrul desenului; contururile nu se scalează cu zoom-ul.
import type { CadruFilm } from "./film";

const cale = (poly: number[][]) => "M" + poly.map((p) => `${p[0]} ${p[1]}`).join("L") + "Z";

const LANE_W = 5.0, STALL_W = 2.5, STALL_D = 5.0, STALL_PARA_L = 5.75;

/** Dreptunghiul unei alei: corpul de `w` m (5 m când cadrul n-o spune) în jurul axei. */
function corpAleii(a: { x1: number; y1: number; x2: number; y2: number; w?: number }): number[][] {
  const dx = a.x2 - a.x1, dy = a.y2 - a.y1;
  const L = Math.hypot(dx, dy) || 1;
  const w = a.w ?? LANE_W;
  const ux = dx / L, uy = dy / L, nx = -uy * (w / 2), ny = ux * (w / 2);
  return [[a.x1 + nx, a.y1 + ny], [a.x2 + nx, a.y2 + ny],
          [a.x2 - nx, a.y2 - ny], [a.x1 - nx, a.y1 - ny]];
}

/** Dreptunghiul unui loc din forma compactă `[cx, cy, deg, forma]`. */
function corpLocului(q: [number, number, number, string]): number[][] {
  const [cx, cy, deg, forma] = q;
  const a = (deg * Math.PI) / 180, ux = Math.cos(a), uy = Math.sin(a);
  const hu = STALL_W / 2, hv = (forma === "para" ? STALL_PARA_L : STALL_D) / 2;
  const nx = -uy, ny = ux;
  return [[cx + ux * hu + nx * hv, cy + uy * hu + ny * hv],
          [cx - ux * hu + nx * hv, cy - uy * hu + ny * hv],
          [cx - ux * hu - nx * hv, cy - uy * hu - ny * hv],
          [cx + ux * hu - nx * hv, cy + uy * hu - ny * hv]];
}

/** Culorile poligoanelor unui cadru de la sol (circulația planului). */
const POLIGON: Record<string, { fill: string; fillOpacity: number; stroke: string;
                                strokeWidth: number; dash?: string }> = {
  verde:     { fill: "#15803d", fillOpacity: 0.35, stroke: "#15803d", strokeWidth: 0.6 },
  zona:      { fill: "#f97316", fillOpacity: 0.40, stroke: "#c2410c", strokeWidth: 1 },
  palier:    { fill: "#991b1b", fillOpacity: 0.12, stroke: "#7f1d1d", strokeWidth: 1.4 },
  gura:      { fill: "#991b1b", fillOpacity: 0.25, stroke: "#7f1d1d", strokeWidth: 1.4 },
  excavatie: { fill: "none", fillOpacity: 0, stroke: "#525252", strokeWidth: 1.2, dash: "8 5" },
};

export function FilmLayer({ cadru }: { cadru: CadruFilm | null }) {
  if (!cadru) return null;
  return (
    <g className="film-impachetare" style={{ pointerEvents: "visiblePainted" }}>
      {/* poligoanele cadrului de la sol — verdele, zonele de acces, palierele
          și gurile, excavația —, SUB axe */}
      {(cadru.poligoane ?? []).map((c, i) => {
        const st = POLIGON[c.fel] ?? POLIGON.excavatie;
        return (
          <path key={`g${i}`} d={cale(c.poly)} className={`film-poligon film-${c.fel}`}
                fill={st.fill} fillOpacity={st.fillOpacity} stroke={st.stroke}
                strokeWidth={st.strokeWidth} strokeDasharray={st.dash}
                style={{ vectorEffect: "non-scaling-stroke" }}>
            <title>{c.label ?? c.fel}</title>
          </path>
        );
      })}
      {/* camerele puse de pas — la ALA, adăposturile (verde) și ieșirile lor
          (portocaliu) —, sub locuri, ca să se vadă de unde au plecat locurile */}
      {(cadru.camere ?? []).map((c, i) => (
        <path key={`c${i}`} d={cale(c.poly)}
              fill={c.kind === "ala" ? "#15803d" : "#ea580c"}
              fillOpacity={c.kind === "ala" ? 0.10 : 0.35}
              stroke={c.kind === "ala" ? "#15803d" : "#c2410c"}
              strokeWidth={c.kind === "ala" ? 2 : 1}
              style={{ vectorEffect: "non-scaling-stroke" }}>
          <title>{c.label ?? c.kind}</title>
        </path>
      ))}
      {/* locurile LUATE de pas: roșu, pe locul pe care îl aveau */}
      {(cadru.pierdute ?? []).map((q, i) => (
        <path key={`p${i}`} d={cale(corpLocului(q))} fill="#b91c1c" fillOpacity={0.30}
              stroke="#b91c1c" strokeWidth={1.2}
              style={{ vectorEffect: "non-scaling-stroke" }}>
          <title>{`luat la pasul «${cadru.pas}»`}</title>
        </path>
      ))}
      {/* locurile pe care le purta rețeaua la pasul ăsta */}
      {(cadru.spatii ?? []).map((q, i) => (
        <path key={`s${i}`} d={cale(corpLocului(q))} fill="#0f766e" fillOpacity={0.14}
              stroke="#0f766e" strokeWidth={0.7} strokeOpacity={0.55}
              style={{ vectorEffect: "non-scaling-stroke" }} />
      ))}
      {/* locurile PUSE de pasul ăsta: violet plin, ca aleile noi, peste cele
          purtate — ca numărul din titlu să se poată număra pe desen */}
      {(cadru.noi ?? []).map((q, i) => (
        <path key={`n${i}`} d={cale(corpLocului(q))} fill="#7c3aed" fillOpacity={0.45}
              stroke="#5b21b6" strokeWidth={1.6}
              style={{ vectorEffect: "non-scaling-stroke" }}>
          <title>{`pus la pasul «${cadru.pas}»`}</title>
        </path>
      ))}
      {/* aleile scoase de pasul ăsta: punctat roșu, pe locul pe care îl aveau */}
      {cadru.scoase.map((a, i) => (
        <path key={`x${i}`} d={cale(corpAleii(a))} fill="#b91c1c" fillOpacity={0.10}
              stroke="#b91c1c" strokeWidth={1.4} strokeDasharray="6 4"
              style={{ vectorEffect: "non-scaling-stroke" }}>
          <title>{`scoasă la pasul «${cadru.pas}»`}</title>
        </path>
      ))}
      {/* rețeaua: aleile noi îngroșate violet, cele de dinainte subțiri albastre */}
      {cadru.alei.map((a, i) => (
        <path key={`a${i}`} d={cale(corpAleii(a))}
              fill={a.noua ? "#7c3aed" : "#1d5fbf"} fillOpacity={a.noua ? 0.30 : 0.12}
              stroke={a.noua ? "#7c3aed" : "#1d5fbf"} strokeWidth={a.noua ? 2.4 : 1}
              style={{ vectorEffect: "non-scaling-stroke" }}>
          <title>{`${a.noua ? "adăugată aici" : "era deja"}${a.sursa ? ` · ${a.sursa}` : ""}`}</title>
        </path>
      ))}
      {/* axele, ca să se vadă rețeaua și când corpurile se suprapun */}
      {cadru.alei.map((a, i) => (
        <line key={`l${i}`} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2}
              stroke={a.noua ? "#5b21b6" : "#153f80"} strokeWidth={a.noua ? 2 : 0.9}
              style={{ vectorEffect: "non-scaling-stroke" }} />
      ))}
    </g>
  );
}
